import { isBefore } from 'date-fns';
import { isEqual } from 'ohash';

/**
 * Transform booking parts into a a group of refundable items for an P2P parts.
 * Group reservations + admissions as group per passenger
 */
export function transformRefundableGroupsP2P(bookingParts: ApiBookingPart[], response: ApiSchema['BookingResponse']) {
    const apiBooking = response?.booking;
    if (!apiBooking) {
        return [];
    }

    const list = bookingParts
        .filter(bp => bp.objectType === 'Admission' || bp.objectType === 'Reservation')
        .reduce<RefundableGroupP2P[]>((list, bookingPart) => {
            if (bookingPart.objectType === 'Admission') {
                assignAdmission(list, bookingPart, apiBooking);
            }
            if (bookingPart.objectType === 'Reservation') {
                assignReservation(list, bookingPart, apiBooking);
            }
            return list;
        }, []);

    list.forEach((refundableGroup) => {
        refundableGroup.refundables = refundableGroup.refundables
            .slice() // create a shallow copy to avoid mutating the original array when sorting
            .sort((a, b) => {
                // 1. Sort by trip.departureDate (earlier first)
                if (a.trip && b.trip && a.trip.departureTime && b.trip.departureTime) {
                    if (isBefore(a.trip.departureTime, b.trip.departureTime))
                        return -1;
                    if (isBefore(b.trip.departureTime, a.trip.departureTime))
                        return 1;
                }

                // 2. Sort by type: "Admission" before "Reservation"
                if (a.type === 'Admission' && b.type === 'Reservation')
                    return -1;
                if (a.type === 'Reservation' && b.type === 'Admission')
                    return 1;

                return 0;
            });
    });

    return list;
}

/**
 *
 */
function assignAdmission(
    list: RefundableGroup[],
    admission: ApiSchema['Admission'],
    apiBooking: ApiSchema['Booking'],
) {
    const type = 'P2P';
    const refundable = transformRefundableAdmission(admission, apiBooking);

    let refundableGroup = list
        .filter(rg => rg.type === type)
        .find(rg => isEqual(rg.passengers, refundable.passengers));
    if (!refundableGroup) {
        refundableGroup = createRefundableGroup(refundable.passengers);
        list.push(refundableGroup);
    }

    refundableGroup.refundables.push(refundable);
}

/**
 *
 */
function assignReservation(
    list: RefundableGroup[],
    reservation: ApiSchema['Reservation'],
    apiBooking: ApiSchema['Booking'],
) {
    const type = 'P2P';
    const refundableReservation = transformRefundableReservation(reservation, apiBooking);

    let refundableGroup = list
        .filter(rg => rg.type === type)
        .find(rg => isEqual(rg.passengers, refundableReservation.passengers));
    if (!refundableGroup) {
        refundableGroup = createRefundableGroup(refundableReservation.passengers);
        list.push(refundableGroup);
    }

    refundableGroup.refundables.push(refundableReservation);
}

/**
 *
 */
function createRefundableGroup(
    passengers: Passenger[],
): RefundableGroupP2P {
    const type = 'P2P';

    return {
        id: passengers.map(p => p.id).join(','),
        passengers,
        type,
        refundables: [],
    };
}
